import { forwardRef, ReactNode, useEffect, useRef, useState } from "react";

import {
  Dialog as MuiDialog,
  DialogTitle as MuiDialogTitle,
  DialogActions as MuiDialogActions,
  Box,
  ThemeProvider,
  Slide,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { TransitionProps } from "@mui/material/transitions";

import { StorybookDialogPreview } from "./StorybookDialogPreview";

import { useTranslate } from "@/common/hooks/useTranslate";
import { FormButton } from "@/forms-design-system/FormButton";
import { FormDivider } from "@/forms-design-system/FormDivider";
import { FormIcon } from "@/forms-design-system/FormIcon/FormIcon";
import { FormTypography } from "@/forms-design-system/FormTypography";
import { InvalidThemeErrorMessage } from "@/forms-design-system/InvalidThemeErrorMessage";
import { useDynamicCanvasTheme } from "@/forms-design-system/Tokens/dynamicCanvasTheme";
import { Cross } from "@/icons/outlined";

export type FormDialogSize = "xs" | "s" | "m" | "l";
export type FormDialogVariant = "default" | "popup" | "drawer";

const DIALOG_SIZE_MAP: Record<FormDialogSize, number> = {
  xs: 444,
  s: 600,
  m: 900,
  l: 1140,
};

const POPUP_WIDTH = 272;

const SlideUpTransition = forwardRef(function SlideUpTransition(
  props: TransitionProps & { children: React.ReactElement },
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

type FormDialogLayoutProps = {
  noPadding?: boolean;
  variant?: FormDialogVariant;
  slots: {
    icon?: ReactNode;
    closeButton: ReactNode;
    title: ReactNode;
    content: ReactNode;
    actions?: ReactNode[];
  };
  slotProps?: {
    content?: {
      id?: string;
    };
    actions?: {
      direction?: "horizontal" | "vertical";
    };
  };
};

export const FormDialogLayout = ({
  noPadding = false,
  variant = "default",
  slots,
  slotProps,
}: FormDialogLayoutProps) => {
  const theme = useTheme();
  const isMobile = variant === "popup" || variant === "drawer";
  const actionsDirection = slotProps?.actions?.direction ?? (isMobile ? "vertical" : "horizontal");
  const contentRef = useRef<HTMLDivElement>(null);
  const [isContentScrollable, setIsContentScrollable] = useState(false);

  // Check if the content of the dialog is scrollable
  useEffect(() => {
    const el = contentRef.current;
    if (!el) {
      return;
    }

    const checkOverflow = () => setIsContentScrollable(el.scrollHeight > el.clientHeight);
    const observer = new ResizeObserver(checkOverflow);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <>
      <MuiDialogTitle
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          padding: variant === "popup" ? theme.spacing(3, 2, 2, 2) : theme.spacing(3, 2, 1.5, 3),
        }}
      >
        {slots.icon ? (
          <>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
              <Box
                sx={{
                  padding: 0.75,
                  color: theme.palette.formsComponents.dialog.iconColor,
                  backgroundColor: theme.palette.formsComponents.dialog.iconBackgroundColor,
                  borderRadius: theme.radius(1),
                  display: "flex",
                  alignItems: "center",
                  overflow: "hidden",
                }}
              >
                {slots.icon}
              </Box>
              {slots.closeButton}
            </Box>
            <FormTypography variant="h6" color={theme.palette.formsComponents.dialog.titleColor}>
              {slots.title}
            </FormTypography>
          </>
        ) : (
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 2 }}>
            <FormTypography
              variant="h6"
              color={theme.palette.formsComponents.dialog.titleColor}
              sx={{ paddingBlock: 0.5 }}
            >
              {slots.title}
            </FormTypography>
            {slots.closeButton}
          </Box>
        )}
      </MuiDialogTitle>
      {isContentScrollable && <FormDivider />}
      <Box
        ref={contentRef}
        id={slotProps?.content?.id}
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
          padding: noPadding ? 0 : theme.spacing(isContentScrollable ? 2 : 0, 3, slots.actions ? 1 : 3, 3),
          ...(variant === "drawer" && slots.actions && { paddingBottom: "112px" }),
          ...theme.typography.body2,
          color: theme.palette.formsComponents.dialog.textColor,
          overflowY: "auto",
          scrollbarColor: `${theme.palette.formsComponents.global.scrollbarThumbColor} transparent`,
          ["&::-webkit-scrollbar-track"]: {
            background: "transparent",
          },
          ["&::-webkit-scrollbar-thumb"]: {
            background: theme.palette.formsComponents.global.scrollbarThumbColor,
          },
        }}
      >
        {slots.content}
      </Box>
      {isContentScrollable && slots.actions && <FormDivider />}
      {slots.actions && (
        <MuiDialogActions
          disableSpacing
          sx={{
            flexDirection: actionsDirection === "vertical" ? "column" : { xs: "column", md: "row-reverse" },
            alignItems: actionsDirection === "vertical" ? "stretch" : { xs: "stretch", md: "center" },
            justifyContent: actionsDirection === "vertical" ? "stretch" : { xs: "stretch", md: "flex-start" },
            gap: 1,
            padding: theme.spacing(2),
            ...(variant === "drawer" && {
              position: "absolute",
              bottom: 0,
              left: 0,
              right: 0,
              backgroundColor: theme.palette.formsComponents.dialog.backgroundColor,
            }),
          }}
        >
          {slots.actions}
        </MuiDialogActions>
      )}
    </>
  );
};

export type FormDialogProps = Pick<FormDialogLayoutProps, "slotProps"> & {
  title: ReactNode;
  children: ReactNode;
  icon?: ReactNode;
  actions?: ReactNode[];
  open?: boolean;
  onClose?: () => void;
  inline?: boolean;
  noPadding?: boolean;
  size?: FormDialogSize;
  variant?: FormDialogVariant;
  hideCloseButton?: boolean;
};

export const InnerFormDialog = forwardRef<HTMLDivElement, FormDialogProps>(
  (
    {
      inline,
      open = false,
      title,
      children,
      icon,
      actions,
      noPadding,
      slotProps,
      size,
      variant = "default",
      hideCloseButton = false,
      ...rest
    },
    ref
  ) => {
    const theme = useTheme();
    const { t } = useTranslate();

    if (!theme.palette.forms || !theme.palette.formsComponents?.dialog) {
      return <InvalidThemeErrorMessage />;
    }

    const borderColor = theme.palette.formsComponents.dialog.borderColor;
    const dialogBg = theme.palette.formsComponents.dialog.backgroundColor;
    const backdropColor = theme.palette.formsComponents.dialog.backdropColor;

    const getWidth = () => {
      if (variant === "popup") {
        return POPUP_WIDTH;
      }
      if (variant === "drawer") {
        return undefined;
      }
      return size ? DIALOG_SIZE_MAP[size] : undefined;
    };

    const dialogWidth = getWidth();

    const content = (
      <FormDialogLayout
        noPadding={noPadding}
        variant={variant}
        slots={{
          icon: icon ? <FormIcon size="large">{icon}</FormIcon> : undefined,
          closeButton: hideCloseButton ? null : (
            <FormButton
              variant="ghost"
              startIcon={<Cross />}
              onClick={rest.onClose}
              aria-label={t("common", "close")}
              iconButton
              sx={{ flexShrink: 0 }}
            />
          ),
          title,
          content: children,
          actions,
        }}
        slotProps={slotProps}
      />
    );

    if (inline) {
      return (
        <StorybookDialogPreview ref={ref} dialogWidth={dialogWidth} variant={variant} {...rest}>
          {content}
        </StorybookDialogPreview>
      );
    }

    if (variant === "drawer") {
      return (
        <MuiDialog
          ref={ref}
          open={open}
          {...rest}
          fullWidth
          maxWidth={false}
          TransitionComponent={SlideUpTransition}
          sx={{
            ["& .MuiBackdrop-root"]: {
              backgroundColor: backdropColor,
            },
            ["& .MuiDialog-container"]: {
              alignItems: "flex-end",
            },
            ["& .MuiPaper-root"]: {
              position: "relative",
              width: "100%",
              maxWidth: "100%",
              margin: 0,
              maxHeight: "80vh",
              ["@supports (max-height: 80dvh)"]: {
                maxHeight: "80dvh",
              },
              borderRadius: "20px 20px 0 0",
              overflowY: "auto",
              backgroundColor: dialogBg,
              ...(borderColor && { border: `1px solid ${borderColor}`, borderBottom: "none" }),
            },
          }}
        >
          {content}
        </MuiDialog>
      );
    }

    return (
      <MuiDialog
        ref={ref}
        open={open}
        {...rest}
        maxWidth={false}
        sx={{
          ["& .MuiBackdrop-root"]: {
            backgroundColor: backdropColor,
          },
          ["& .MuiPaper-root"]: {
            width: "100%",
            ...(dialogWidth && { maxWidth: dialogWidth }),
            overflowY: "visible",
            backgroundColor: dialogBg,
            ...(borderColor && { border: `1px solid ${borderColor}` }),
          },
        }}
      >
        {content}
      </MuiDialog>
    );
  }
);

InnerFormDialog.displayName = "InnerFormDialog";

export const FormDialog = forwardRef<HTMLDivElement, FormDialogProps>((props, ref) => {
  const theme = useTheme();

  if (!theme.palette.forms || !theme.palette.metadata) {
    return <InvalidThemeErrorMessage />;
  }

  // This hook is rendered conditionally, but that should never happen unless in we're in dev mode
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const mode = theme.palette.metadata.mode ?? "light";
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const dialogTheme = useDynamicCanvasTheme(theme.palette.metadata.baseColor, mode);

  return (
    <ThemeProvider theme={dialogTheme}>
      <InnerFormDialog ref={ref} {...props} />
    </ThemeProvider>
  );
});

FormDialog.displayName = "FormDialog";
